import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import { getAllCases, getAllPartnerCases } from "../../store/common/casesSlice";
import { fetchSearchCasesByCaseIdAndCaseOwnerAndStatus } from "../../store/common/searchSlice";
import { CaseType } from "../../types/Case";
import { smilePortalCaseHref } from "../../utils/linkedPortalCase";
import { useAppSelector } from "../../store/hooks";
import { Helmet } from "react-helmet-async";
import { isUrisBrand } from "../../utils/brandLogo";
import {
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaTimes,
  FaFolderOpen,
  FaInbox,
} from "react-icons/fa";

const ACCENT = "#2fa9bf";

const statusOptions = [
  { value: "All", label: "All statuses" },
  { value: "Submitted", label: "Submitted" },
  { value: "Completed", label: "Completed" },
  { value: "Files Pending", label: "Files Pending" },
  { value: "query pending", label: "Query Pending" },
  { value: "under process", label: "Under Process" },
  { value: "Submitted For Milling", label: "Submitted For Milling" },
];

const statusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "files pending")
    return "bg-red-50 text-red-700 ring-red-200";
  if (s === "submitted")
    return "bg-orange-50 text-orange-700 ring-orange-200";
  if (s === "query pending")
    return "bg-amber-50 text-amber-800 ring-amber-200";
  if (s === "under process")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s === "completed")
    return "bg-sky-50 text-sky-700 ring-sky-200";
  if (s.includes("milling"))
    return "bg-violet-50 text-violet-700 ring-violet-200";
  return "bg-gray-50 text-gray-600 ring-gray-200";
};

const urisStatusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "completed") {
    return {
      pill: "bg-[rgba(127,199,154,0.12)] text-[#8fd3a8]",
      dot: "bg-[#8fd3a8]",
    };
  }
  if (s === "submitted" || s.includes("milling")) {
    return {
      pill: "bg-[rgba(229,164,67,0.13)] text-[#EDB766]",
      dot: "bg-[#EDB766]",
    };
  }
  if (s.includes("hold") || s.includes("pending") || s.includes("query")) {
    return {
      pill: "bg-[rgba(191,30,46,0.16)] text-[#F3A0A6]",
      dot: "bg-[#F06A74]",
    };
  }
  return {
    pill: "bg-[rgba(156,154,155,0.16)] text-[#d6d4d5]",
    dot: "bg-[#d6d4d5]",
  };
};

const formatStatusLabel = (status: string) => {
  if (!status) return "—";
  if (status.toLowerCase() === "query pending") return "Query Pending";
  if (status.toLowerCase() === "under process") return "Under Process";
  return status;
};

const isFilledOrder = (serviceType?: string) =>
  (serviceType ?? "")
    .split(",")
    .map((s) => s.trim())
    .includes("filledOrder");

const formatCreation = (ms: number) => {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Cases: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAppSelector((state) => state.auth);
  const {
    cases: allCases,
    isLoading: allCasesLoading,
    error: allCasesError,
    totalPages,
    partnerCases,
    partnerTokens,
  } = useAppSelector((state) => state.adminCases);
  const {
    combined: searchResults,
    loading: searchLoading,
    error: searchError,
  } = useAppSelector((state) => state.search);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [tokenFilter, setTokenFilter] = useState("All");
  const isUris = isUrisBrand(user);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, statusFilter, tokenFilter]);

  const isSearching = useMemo(
    () => !!debouncedSearchTerm.trim() || statusFilter !== "All",
    [debouncedSearchTerm, statusFilter],
  );

  const hasActiveFilters =
    !!searchTerm.trim() || statusFilter !== "All" || tokenFilter !== "All";

  const casesToShow: CaseType[] = useMemo(() => {
    if (user?.role === "Side Admin") return partnerCases ?? [];
    if (isSearching) return Array.isArray(searchResults) ? searchResults : [];
    return allCases ?? [];
  }, [user, partnerCases, isSearching, searchResults, allCases]);

  const isLoading = useMemo(() => {
    if (user?.role === "Side Admin") return allCasesLoading;
    if (isSearching) return searchLoading.combined;
    return allCasesLoading;
  }, [user, allCasesLoading, isSearching, searchLoading]);

  const errorMsg = useMemo(() => {
    if (user?.role === "Side Admin") return allCasesError;
    if (isSearching) return searchError.combined;
    return allCasesError;
  }, [user, allCasesError, isSearching, searchError]);

  useEffect(() => {
    if (!user) return;

    if (user.role === "Side Admin") {
      dispatch(
        getAllPartnerCases({
          page,
          search: debouncedSearchTerm.trim(),
          status: statusFilter === "All" ? "" : statusFilter,
          token: isUris && tokenFilter !== "All" ? tokenFilter : "",
          userID: user.user_id.toString(),
        }),
      );
    } else if (isSearching) {
      dispatch(
        fetchSearchCasesByCaseIdAndCaseOwnerAndStatus({
          caseOwner: debouncedSearchTerm.trim() || undefined,
          status: statusFilter === "All" ? undefined : statusFilter,
        }),
      );
    } else {
      dispatch(getAllCases({ page, search: "", status: "" }));
    }
  }, [dispatch, page, debouncedSearchTerm, statusFilter, tokenFilter, user, isSearching, isUris]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setStatusFilter("All");
    setTokenFilter("All");
    setPage(1);
  };

  const canViewBox = !["production", "admin read", "side admin"].includes(
    user?.role?.toLowerCase() || "",
  );

  const colCount = isUris ? 8 : 7;
  const sideAdminPaging = user?.role === "Side Admin";

  return (
    <>
      <Helmet>
        <title>Admin Cases</title>
      </Helmet>

      <div
        className={`flex w-full min-w-0 flex-col gap-4 text-sm ${
          isUris ? "uris-scope gap-4 text-[#eceaeb]" : ""
        }`}
      >
        {/* Header */}
        <div
          className={`flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6 lg:flex-row ${
            isUris
              ? "rounded-[14px] border border-[#2e2f34] border-t-[3px] border-t-[#BF1E2E] bg-[#1d1e21] px-[22px] py-5"
              : "rounded-2xl"
          }`}
          style={isUris ? undefined : { backgroundColor: ACCENT }}
        >
          <div className="min-w-0">
            <h1
              className={`truncate font-bold ${
                isUris
                  ? "font-[Montserrat,sans-serif] text-xl text-white"
                  : "text-lg text-white sm:text-xl"
              }`}
            >
              Admin Cases
            </h1>
            <p
              className={`mt-1 ${
                isUris ? "text-[13px] text-[#9C9A9B]" : "mt-0.5 text-xs text-white/85 sm:text-sm"
              }`}
            >
              {isUris
                ? "Browse, search and open case workspaces across all clinics."
                : "Browse, search, and open case workspaces"}
            </p>
          </div>

          <div className="flex w-full min-w-0 flex-col flex-wrap items-stretch gap-2.5 sm:flex-row sm:items-center lg:w-auto">
            <div className="relative w-full sm:w-[260px]">
              <FaSearch
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs ${
                  isUris ? "text-[#8a888b]" : "text-[#8A8A85]"
                }`}
              />
              <input
                type="text"
                placeholder={isUris ? "Search case ID / owner" : "Search Case ID / Owner…"}
                className={`w-full outline-none transition ${
                  isUris
                    ? "h-[42px] rounded-lg border border-[#37383e] bg-[#141517] py-0 pl-[34px] pr-9 text-[13.5px] text-[#f2f0f1] placeholder:text-[#77757a] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)]"
                    : "rounded-xl border border-white bg-white py-2.5 pl-9 pr-9 text-[#1A1A18] placeholder:text-[#8A8A85] shadow-sm focus:ring-2 focus:ring-white/70"
                }`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#4A4A48]"
                >
                  <FaTimes className="text-xs" />
                </button>
              ) : null}
            </div>

            {isUris ? (
              <select
                className="h-[42px] w-full cursor-pointer rounded-lg border border-[#37383e] bg-[#141517] px-3 text-[13.5px] text-[#f2f0f1] outline-none [color-scheme:dark] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)] sm:w-[160px] [&>option]:text-gray-800"
                value={tokenFilter}
                onChange={(e) => setTokenFilter(e.target.value)}
              >
                <option value="All">All tokens</option>
                {partnerTokens.map((token) => (
                  <option key={token} value={token}>
                    {token}
                  </option>
                ))}
              </select>
            ) : null}

            <select
              className={`cursor-pointer outline-none [&>option]:text-gray-800 ${
                isUris
                  ? "h-[42px] w-full rounded-lg border border-[#37383e] bg-[#141517] px-3 text-[13.5px] text-[#f2f0f1] [color-scheme:dark] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)] sm:w-[160px]"
                  : "w-full rounded-xl border border-white bg-white px-3 py-2.5 text-[#1A1A18] shadow-sm focus:ring-2 focus:ring-white/70 sm:w-auto"
              }`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Meta row */}
        <div
          className={`flex flex-wrap items-center justify-between gap-3 px-0.5 ${
            isUris ? "text-[12.5px] text-[#9C9A9B]" : ""
          }`}
        >
          <div
            className={`flex flex-wrap items-center gap-3 ${
              isUris ? "" : "text-xs text-[#8A8A85]"
            }`}
          >
            {isLoading ? (
              <span>Loading cases…</span>
            ) : (
              <span>
                {casesToShow.length} case
                {casesToShow.length === 1 ? "" : "s"}
                {sideAdminPaging || !isSearching ? " on this page" : " matched"}
              </span>
            )}
            {isSearching && !sideAdminPaging ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-amber-800 ring-1 ring-amber-200">
                Search shows up to 100 recent records
              </span>
            ) : null}
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={`inline-flex items-center gap-1.5 font-medium ${
                  isUris
                    ? "text-[#F3A0A6] hover:text-white"
                    : "text-[#2fa9bf] hover:text-[#2296aa]"
                }`}
              >
                <FaTimes className="text-[10px]" />
                Clear filters
              </button>
            ) : null}
          </div>

          {sideAdminPaging || !isSearching ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={() => handlePageChange(page - 1)}
                className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  isUris
                    ? "border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white"
                    : "h-8 w-8 border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
                }`}
                aria-label="Previous page"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span
                className={`min-w-[5.5rem] text-center text-xs tabular-nums ${
                  isUris ? "text-[#9C9A9B]" : "text-[#4A4A48]"
                }`}
              >
                Page {totalPages === 0 ? 0 : page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || isLoading || totalPages === 0}
                onClick={() => handlePageChange(page + 1)}
                className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  isUris
                    ? "border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white"
                    : "h-8 w-8 border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
                }`}
                aria-label="Next page"
              >
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          ) : null}
        </div>

        {/* Table */}
        <div
          className={
            isUris
              ? "overflow-x-auto rounded-[14px] border border-[#2e2f34] bg-[#1d1e21]"
              : "overflow-x-auto rounded-2xl border border-[#E8E8E4] bg-white shadow-sm"
          }
        >
          <table className="w-full table-auto">
            <thead>
              <tr
                className={
                  isUris
                    ? "border-b border-[#2e2f34] bg-[#1a1b1e]"
                    : "border-b border-[#E8E8E4] bg-[#FDFBF2]"
                }
              >
                <th
                  className={`px-[18px] py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.08em] ${
                    isUris ? "text-[#8a888b]" : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  Case ID
                </th>
                {isUris ? (
                  <th className="px-[18px] py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a888b]">
                    Token
                  </th>
                ) : null}
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  Smile case
                </th>
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  {isUris ? "Case owner" : "Case Owner"}
                </th>
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  {isUris ? "Creation" : "Creation"}
                </th>
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  Status
                </th>
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  {isUris ? "Files" : "Box"}
                </th>
                <th
                  className={`text-left text-[11px] uppercase ${
                    isUris
                      ? "px-[18px] py-3.5 font-bold tracking-[0.08em] text-[#8a888b]"
                      : "px-5 py-3 font-semibold tracking-wider text-[#8A8A85]"
                  }`}
                >
                  Pending
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <span
                        className="h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
                        aria-hidden
                      />
                      Loading cases…
                    </div>
                  </td>
                </tr>
              ) : errorMsg ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-red-500"
                  >
                    Couldn’t load cases. Please try again.
                  </td>
                </tr>
              ) : casesToShow.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <FaInbox className="text-2xl text-[#DDDDD8]" />
                      <span>No cases found</span>
                      {hasActiveFilters ? (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-[#2fa9bf] hover:underline"
                        >
                          Clear filters
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : (
                casesToShow.map((item) => {
                  const filled = isFilledOrder(item.service_type);
                  const pending = item.pending_comments_admin ?? 0;

                  return (
                    <tr
                      key={item.case_id}
                      className={`border-b last:border-0 transition-colors ${
                        isUris
                          ? "border-[#26272b] hover:bg-[#222327]"
                          : filled
                            ? "border-[#F0F0EC] bg-sky-50/70 hover:bg-sky-50"
                            : "border-[#F0F0EC] hover:bg-[#FAFAF8]"
                      }`}
                    >
                      <td
                        className={`align-middle ${
                          isUris ? "h-16 px-[18px] py-3" : "px-5 py-3.5"
                        }`}
                      >
                        <div className="flex min-w-0 flex-col items-start gap-1.5">
                          <a
                            href={`/dashboard/admin/cases/${item.case_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`break-all font-bold tabular-nums no-underline hover:underline ${
                              isUris
                                ? "text-[#F3A0A6] hover:text-white"
                                : "font-semibold text-[#2fa9bf] hover:text-[#2296aa]"
                            }`}
                          >
                            {item.case_id}
                          </a>
                          {filled ? (
                            <span
                              className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                                isUris
                                  ? "border border-[#3a3b41] bg-[#26272b] text-[#bdbbbc]"
                                  : "bg-sky-100 px-1.5 text-[10px] font-medium text-sky-800 ring-1 ring-sky-200"
                              }`}
                            >
                              Filled order
                            </span>
                          ) : null}
                        </div>
                      </td>
                      {isUris ? (
                        <td className="h-16 px-[18px] py-3 align-middle">
                          {item.lab_token ? (
                            <span className="inline-flex h-6 min-w-[38px] items-center justify-center rounded-md border border-[#3a3b41] bg-[#26272b] px-2 text-xs font-bold tracking-wide text-[#e9e7e8]">
                              {item.lab_token}
                            </span>
                          ) : (
                            <span className="text-[#5f5d60]">—</span>
                          )}
                        </td>
                      ) : null}
                      <td
                        className={`align-middle ${
                          isUris ? "h-16 px-[18px] py-3" : "px-5 py-3.5"
                        }`}
                      >
                        {item.smile_id ? (
                          <a
                            href={smilePortalCaseHref(item.smile_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`break-all font-semibold hover:underline ${
                              isUris
                                ? "text-[#F3A0A6] hover:text-white"
                                : "text-[#2fa9bf] hover:text-[#2296aa]"
                            }`}
                          >
                            {item.smile_id}
                          </a>
                        ) : (
                          <span className={isUris ? "text-[#5f5d60]" : "text-[#AAAAAA]"}>
                            —
                          </span>
                        )}
                      </td>
                      <td
                        className={`max-w-[16rem] whitespace-normal break-words align-middle ${
                          isUris
                            ? "h-16 px-[18px] py-3 font-semibold text-white"
                            : "px-5 py-3.5 text-[#4A4A48]"
                        }`}
                      >
                        {item.case_owner || "—"}
                      </td>
                      <td
                        className={`align-middle tabular-nums ${
                          isUris
                            ? "h-16 px-[18px] py-3 text-[#bdbbbc]"
                            : "px-5 py-3.5 text-[#4A4A48]"
                        }`}
                      >
                        {formatCreation(item.creation_time_ms)}
                      </td>
                      <td
                        className={`align-middle ${
                          isUris ? "h-16 px-[18px] py-3" : "px-5 py-3.5"
                        }`}
                      >
                        {isUris ? (
                          <span
                            className={`inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ${
                              urisStatusStyles(item.status).pill
                            }`}
                          >
                            <span
                              className={`block h-1.5 w-1.5 rounded-full ${
                                urisStatusStyles(item.status).dot
                              }`}
                            />
                            {formatStatusLabel(item.status)}
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${statusStyles(
                              item.status,
                            )}`}
                          >
                            {formatStatusLabel(item.status)}
                          </span>
                        )}
                      </td>
                      <td
                        className={`align-middle ${
                          isUris ? "h-16 px-[18px] py-3" : "px-5 py-3.5"
                        }`}
                      >
                        {canViewBox && item.box_folder_id ? (
                          <a
                            href={`https://app.box.com/folder/${item.box_folder_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 font-semibold no-underline ${
                              isUris
                                ? "text-[#F3A0A6] hover:text-white"
                                : "font-medium text-[#2fa9bf] hover:text-[#2296aa]"
                            }`}
                          >
                            <FaFolderOpen className="text-xs" />
                            View files
                            <FaExternalLinkAlt className="text-[9px] opacity-70" />
                          </a>
                        ) : (
                          <span
                            className={`inline-flex cursor-not-allowed items-center gap-1.5 ${
                              isUris ? "text-[#5f5d60]" : "text-[#AAAAAA]"
                            }`}
                          >
                            <FaFolderOpen className="text-xs" />
                            View files
                          </span>
                        )}
                      </td>
                      <td
                        className={`align-middle ${
                          isUris ? "h-16 px-[18px] py-3" : "px-5 py-3.5"
                        }`}
                      >
                        {pending > 0 ? (
                          <span
                            className={`inline-flex items-center justify-center rounded-full text-xs font-bold ${
                              isUris
                                ? "h-7 w-7 border border-[rgba(229,164,67,0.55)] bg-[rgba(229,164,67,0.16)] text-[#EDB766]"
                                : "h-7 min-w-[1.75rem] rounded-full bg-amber-100 px-2 font-semibold text-amber-800 ring-1 ring-amber-200"
                            }`}
                          >
                            {pending}
                          </span>
                        ) : (
                          <span className={isUris ? "text-[#5f5d60]" : "text-[#AAAAAA]"}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom pagination */}
        {(sideAdminPaging || !isSearching) && totalPages > 1 ? (
          <div className="flex items-center justify-center gap-2 pb-1">
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={() => handlePageChange(page - 1)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium ${
                isUris
                  ? "border-[#3a3c42] bg-[#1a1b1f] text-white hover:border-[#c31c30]"
                  : "border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
              }`}
            >
              <FaChevronLeft className="text-[10px]" />
              Prev
            </button>
            <span className="text-xs text-[#8A8A85] tabular-nums px-2">
              {totalPages === 0 ? 0 : page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || isLoading}
              onClick={() => handlePageChange(page + 1)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium ${
                isUris
                  ? "border-[#3a3c42] bg-[#1a1b1f] text-white hover:border-[#c31c30]"
                  : "border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
              }`}
            >
              Next
              <FaChevronRight className="text-[10px]" />
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Cases;
